import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { currentLocaleTag } from '../../lib/formatting'
import {
  AlertCircle,
  Coins,
  Edit2,
  FileText,
  Loader2,
  Pause,
  Play,
  Plus,
  RotateCw,
  Send,
  Trash2,
} from 'lucide-react'
import { api, ApiError } from '../../lib/api'
import { confirmDialog, toast } from '../../lib/dialogs'
import { SELECTOR_PAGE_SIZE } from '../../lib/constants'
import Modal from '../../components/ui/Modal'
import RatePickerModal from '../../components/rates/RatePickerModal'
import { formatCents, parseEurosToCents, centsToEurosInput } from '../../lib/money'
import {
  RecurringInvoicePeriodicityLabel,
  type ClientListResponse,
  type CreateRecurringInvoiceInput,
  type OpportunityListResponse,
  type ProductDto,
  type RatePickerItem,
  type RecurringInvoiceDto,
  type RecurringInvoiceLineInput,
  type RecurringInvoiceListResponse,
  type RecurringInvoicePeriodicityT,
  type RecurringInvoiceStatusT,
} from '@toga/shared'

const STATUS_STYLES: Record<RecurringInvoiceStatusT, string> = {
  ACTIVE: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/40',
  PAUSED: 'bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/40',
  CANCELED: 'bg-slate-500/15 t-secondary border-slate-500/30',
  COMPLETED: 'bg-sky-500/15 text-sky-700 dark:text-sky-300 border-sky-500/40',
}

export default function RecurringSettingsPage() {
  const { t } = useTranslation('settings')
  const [items, setItems] = useState<RecurringInvoiceDto[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [editing, setEditing] = useState<RecurringInvoiceDto | 'new' | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const resp = await api.get<RecurringInvoiceListResponse>('/recurring-invoices')
      setItems(resp.items)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const action = async (id: string, op: 'pause' | 'resume' | 'cancel' | 'generate-now') => {
    try {
      const r = await api.post<{ invoiceId?: string }>(`/recurring-invoices/${id}/${op}`, {})
      if (op === 'generate-now' && r?.invoiceId) {
        if (await confirmDialog(t('recurring.draftCreatedConfirm'))) {
          window.location.href = `/billing/${r.invoiceId}`
          return
        }
      }
      await load()
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as { error?: string }) : undefined
      toast.error(body?.error ?? (err instanceof Error ? err.message : 'Error'))
    }
  }

  const onDelete = async (r: RecurringInvoiceDto) => {
    if (
      !(await confirmDialog({
        message: t('recurring.deleteConfirm', { name: r.name }),
        tone: 'danger',
      }))
    )
      return
    try {
      await api.delete(`/recurring-invoices/${r.id}`)
      await load()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Error')
    }
  }

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-serif font-semibold t-primary">{t('recurring.title')}</h2>
          <p className="t-muted text-sm mt-1">
            {t('recurring.subtitlePre')}{' '}
            <strong>{t('recurring.subtitleDraft')}</strong> {t('recurring.subtitlePost')}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setEditing('new')}
          className="px-3 py-1.5 rounded-lg text-sm font-semibold text-white bg-toga-600 hover:bg-toga-700 hover:shadow-glow-toga flex items-center gap-1.5"
        >
          <Plus size={14} /> {t('recurring.newButton')}
        </button>
      </header>

      {error && (
        <div className="surface p-3 text-sm rounded-lg border text-rose-700 dark:text-rose-300 bg-rose-500/10 border-rose-500/30 flex items-center gap-2">
          <AlertCircle size={14} /> {error}
        </div>
      )}

      <div className="surface overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[var(--bg-input-soft)] text-xs uppercase tracking-wider t-muted">
            <tr>
              <th className="px-3 py-2 text-left">{t('recurring.table.name')}</th>
              <th className="px-3 py-2 text-left">{t('recurring.table.client')}</th>
              <th className="px-3 py-2 text-left">{t('recurring.table.periodicity')}</th>
              <th className="px-3 py-2 text-left">{t('recurring.table.next')}</th>
              <th className="px-3 py-2 text-right">{t('recurring.table.totalPerPeriod')}</th>
              <th className="px-3 py-2 text-center">{t('recurring.table.generated')}</th>
              <th className="px-3 py-2 text-left">{t('recurring.table.status')}</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={8} className="px-3 py-6 text-center t-muted">
                  <Loader2 size={14} className="inline-block animate-spin" /> {t('recurring.loading')}
                </td>
              </tr>
            )}
            {!loading && items.length === 0 && (
              <tr>
                <td colSpan={8} className="px-3 py-10 text-center">
                  <RotateCw size={28} className="mx-auto t-faint mb-2" />
                  <p className="t-muted text-sm">
                    {t('recurring.empty')}
                  </p>
                </td>
              </tr>
            )}
            {items.map((r) => {
              const total = r.lines.reduce(
                (acc, l) => acc + Math.round(Number(l.quantity) * l.unitPriceCents),
                0
              )
              return (
                <tr key={r.id} className="border-t border-base/40 hover:bg-hover">
                  <td className="px-3 py-2 t-primary font-medium">{r.name}</td>
                  <td className="px-3 py-2 t-secondary">
                    <Link to="/clients" className="hover:underline">
                      {r.clientDisplayName}
                    </Link>
                    {r.opportunityCode && (
                      <span className="text-[10px] t-faint font-mono ml-1">
                        ({r.opportunityCode})
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs t-secondary">
                    {t('enums:RecurringInvoicePeriodicity.' + r.periodicity)} {t('recurring.dayParen', { day: r.dayOfMonth })}
                  </td>
                  <td className="px-3 py-2 text-xs t-secondary font-mono">
                    {new Date(r.nextRunDate).toLocaleDateString(currentLocaleTag())}
                  </td>
                  <td className="px-3 py-2 text-right t-primary font-mono">
                    {formatCents(total)}
                  </td>
                  <td className="px-3 py-2 text-center text-xs t-muted">{r.generatedCount}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] border ${STATUS_STYLES[r.status]}`}>
                      {t('enums:RecurringInvoiceStatus.' + r.status)}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="inline-flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => void action(r.id, 'generate-now')}
                        disabled={r.status !== 'ACTIVE'}
                        className="w-7 h-7 grid place-items-center rounded hover:bg-hover t-muted hover:text-emerald-700 dark:hover:text-emerald-300 disabled:opacity-30"
                        title={t('recurring.actions.generateNow')}
                      >
                        <Send size={12} />
                      </button>
                      {r.status === 'ACTIVE' ? (
                        <button
                          type="button"
                          onClick={() => void action(r.id, 'pause')}
                          className="w-7 h-7 grid place-items-center rounded hover:bg-amber-500/10 t-muted hover:text-amber-700 dark:hover:text-amber-300"
                          title={t('recurring.actions.pause')}
                        >
                          <Pause size={12} />
                        </button>
                      ) : r.status === 'PAUSED' ? (
                        <button
                          type="button"
                          onClick={() => void action(r.id, 'resume')}
                          className="w-7 h-7 grid place-items-center rounded hover:bg-emerald-500/10 t-muted hover:text-emerald-700 dark:hover:text-emerald-300"
                          title={t('recurring.actions.resume')}
                        >
                          <Play size={12} />
                        </button>
                      ) : null}
                      <button
                        type="button"
                        onClick={() => setEditing(r)}
                        className="w-7 h-7 grid place-items-center rounded hover:bg-hover t-muted hover:text-toga-700 dark:hover:text-toga-200"
                        title={t('recurring.actions.edit')}
                      >
                        <Edit2 size={12} />
                      </button>
                      <button
                        type="button"
                        onClick={() => void onDelete(r)}
                        className="w-7 h-7 grid place-items-center rounded hover:bg-rose-500/10 t-muted hover:text-rose-400"
                        title={t('recurring.actions.delete')}
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {editing && (
        <RecurringFormModal
          initial={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null)
            await load()
          }}
        />
      )}
    </div>
  )
}

// ---------- Modal alta/edición ----------

interface UiLine extends RecurringInvoiceLineInput {
  _key: string
  unitPriceInput: string
  costInput: string // coste interno por unidad — nunca llega al cliente
}

function RecurringFormModal({
  initial,
  onClose,
  onSaved,
}: {
  initial: RecurringInvoiceDto | null
  onClose: () => void
  onSaved: () => void | Promise<void>
}) {
  const { t } = useTranslation(['settings', 'common'])
  const isEdit = initial !== null

  const [name, setName] = useState(initial?.name ?? '')
  const [clientId, setClientId] = useState(initial?.clientId ?? '')
  // La oportunidad a la que se imputa la iguala. Sin ella el pago recurrente
  // existe (cuelga del cliente) pero NO sale en la pestaña "Hitos de pago" de
  // ninguna oportunidad, que es donde se mira el contrato. Pasó el 10/08/2026
  // con los 3.000 €/año de actualización legal de la Fundación.
  const [opportunityId, setOpportunityId] = useState(initial?.opportunityId ?? '')
  const [opportunities, setOpportunities] = useState<OpportunityListResponse['items']>([])
  const [periodicity, setPeriodicity] = useState<RecurringInvoicePeriodicityT>(
    initial?.periodicity ?? 'MONTHLY'
  )
  const [notes, setNotes] = useState(initial?.notes ?? '')
  const [startDate, setStartDate] = useState(
    initial?.nextRunDate?.slice(0, 10) ?? new Date().toISOString().slice(0, 10)
  )
  const [lines, setLines] = useState<UiLine[]>(() =>
    initial && initial.lines.length > 0
      ? initial.lines.map((l) => ({
          ...l,
          productId: l.productId,
          costCents: l.costCents ?? 0,
          _key: l.id,
          unitPriceInput: centsToEurosInput(l.unitPriceCents),
          costInput: centsToEurosInput(l.costCents ?? 0),
        }))
      : [
          {
            _key: 'init',
            position: 0,
            productId: null,
            description: '',
            quantity: '1',
            unitPriceCents: 0,
            unitPriceInput: '',
            costCents: 0,
            costInput: '',
            discountPct: '0',
            vatRate: '0.21',
            isSupplied: false,
          },
        ]
  )

  const [clients, setClients] = useState<ClientListResponse['items']>([])
  const [products, setProducts] = useState<ProductDto[]>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    // pageSize máx del backend = 100 (antes 200 → 400 y el cliente no salía).
    api
      .get<ClientListResponse>(`/clients?pageSize=${SELECTOR_PAGE_SIZE}&sortBy=name&sortDir=asc`)
      .then((r) => setClients(r.items))
      .catch(() => setClients([]))
  }, [])

  // Oportunidades del cliente elegido. Se recarga al cambiar de cliente porque
  // el backend rechaza una oportunidad que no sea suya (routes/recurring.ts).
  useEffect(() => {
    if (!clientId) {
      setOpportunities([])
      return
    }
    let cancelled = false
    api
      .get<OpportunityListResponse>(`/opportunities?clientId=${clientId}&pageSize=100`)
      .then((r) => {
        if (cancelled) return
        setOpportunities(r.items)
        // Si la que había seleccionada no es de este cliente, se suelta.
        setOpportunityId((cur) => (cur && r.items.some((o) => o.id === cur) ? cur : ''))
      })
      .catch(() => {
        if (!cancelled) setOpportunities([])
      })
    return () => {
      cancelled = true
    }
  }, [clientId])

  // F35 — Productos/servicios del catálogo (solo activos), una vez al montar.
  useEffect(() => {
    let cancelled = false
    api
      .get<{ items: ProductDto[] }>('/products')
      .then((r) => {
        if (cancelled) return
        const sorted = [...r.items].sort(
          (a, b) => a.position - b.position || a.name.localeCompare(b.name)
        )
        setProducts(sorted)
      })
      .catch(() => {
        if (!cancelled) setProducts([])
      })
    return () => {
      cancelled = true
    }
  }, [])

  const totalCents = useMemo(
    () =>
      lines.reduce(
        (acc, l) => acc + Math.round((Number(l.quantity) || 0) * (l.unitPriceCents ?? 0)),
        0
      ),
    [lines]
  )

  const totalCostCents = useMemo(
    () =>
      lines.reduce(
        (acc, l) => acc + Math.round((Number(l.quantity) || 0) * (l.costCents ?? 0)),
        0
      ),
    [lines]
  )

  const updateLine = (idx: number, patch: Partial<UiLine>) => {
    setLines((prev) => {
      const copy = [...prev]
      copy[idx] = { ...copy[idx]!, ...patch }
      return copy
    })
  }

  // F35 — Al elegir un producto del catálogo, autorellenamos descripción/precio/IVA
  // y guardamos el productId. El usuario puede luego sobrescribir cualquier campo.
  const onPickProduct = (idx: number, productId: string) => {
    if (!productId) {
      updateLine(idx, { productId: null })
      return
    }
    const p = products.find((x) => x.id === productId)
    if (!p) return
    updateLine(idx, {
      productId: p.id,
      description: p.name,
      unitPriceCents: p.unitPriceCents,
      unitPriceInput: centsToEurosInput(p.unitPriceCents),
      vatRate: (p.vatRateBps / 10000).toString(),
    })
  }

  const addLine = () => {
    setLines((prev) => [
      ...prev,
      {
        _key: Math.random().toString(36).slice(2),
        position: prev.length,
        productId: null,
        description: '',
        quantity: '1',
        unitPriceCents: 0,
        unitPriceInput: '',
        costCents: 0,
        costInput: '',
        discountPct: '0',
        vatRate: '0.21',
        isSupplied: false,
      },
    ])
  }

  const removeLine = (idx: number) =>
    setLines((prev) => prev.filter((_, i) => i !== idx))

  const [pickerOpen, setPickerOpen] = useState(false)
  const addLineFromRate = (it: RatePickerItem) => {
    setLines((prev) => [
      ...prev,
      {
        _key: Math.random().toString(36).slice(2),
        position: prev.length,
        productId: null,
        description: it.name,
        quantity: '1',
        unitPriceCents: it.appliedAmountCents,
        unitPriceInput: centsToEurosInput(it.appliedAmountCents),
        costCents: 0,
        costInput: '',
        discountPct: '0',
        vatRate: it.vatRate.toString(),
        isSupplied: false,
      },
    ])
    // Si es iguala mensual, forzamos periodicity MENSUAL.
    if (it.kind === 'IGUALA_MENSUAL') setPeriodicity('MONTHLY')
  }

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!name.trim() || !clientId) {
      setError(t('recurring.form.errors.nameClient'))
      return
    }
    if (lines.length === 0 || lines.some((l) => !(l.description ?? '').trim())) {
      setError(t('recurring.form.errors.lineDescription'))
      return
    }
    setBusy(true)
    setError(null)
    try {
      const payload: CreateRecurringInvoiceInput = {
        clientId,
        opportunityId: opportunityId || null,
        // Serie siempre la de por defecto; IRPF omitido (solo aplica a autónomos).
        seriesId: null,
        name: name.trim(),
        notes: notes.trim() || null,
        periodicity,
        // Día de emisión derivado de la "Próxima emisión" (sin campo aparte).
        dayOfMonth: startDate ? Math.min(28, Math.max(1, new Date(startDate).getDate())) : 1,
        startDate: startDate || null,
        lines: lines.map((l, idx) => ({
          position: idx,
          productId: l.productId ?? null,
          description: (l.description ?? '').trim(),
          quantity: l.quantity || '1',
          unitPriceCents: l.unitPriceCents,
          costCents: l.costCents ?? 0,
          discountPct: l.discountPct,
          vatRate: l.vatRate,
          isSupplied: l.isSupplied,
        })),
      }
      if (isEdit && initial) {
        await api.patch(`/recurring-invoices/${initial.id}`, payload)
      } else {
        await api.post('/recurring-invoices', payload)
      }
      await onSaved()
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as { error?: string }) : undefined
      setError(body?.error ?? (err instanceof Error ? err.message : 'Error'))
    } finally {
      setBusy(false)
    }
  }

  const input =
    'w-full bg-transparent border border-base rounded px-2 py-1.5 text-sm t-primary focus:outline-none focus:border-toga-500/50'

  return (
    <Modal open onClose={onClose} title={isEdit ? t('recurring.form.titleEdit') : t('recurring.form.titleNew')} maxWidth="max-w-3xl">
      <form onSubmit={onSubmit} className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-xs t-muted">{t('recurring.form.internalName')}</span>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('recurring.form.internalNamePlaceholder')}
              className={input}
              required
            />
          </label>
          <label className="block">
            <span className="text-xs t-muted">{t('recurring.form.client')}</span>
            <select
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              className={input}
              required
            >
              <option value="">—</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.displayName}
                </option>
              ))}
            </select>
          </label>
        </div>

        <label className="block">
          <span className="text-xs t-muted">{t('recurring.form.opportunity')}</span>
          <select
            value={opportunityId}
            onChange={(e) => setOpportunityId(e.target.value)}
            className={input}
            disabled={!clientId || opportunities.length === 0}
          >
            <option value="">{t('recurring.form.opportunityNone')}</option>
            {opportunities.map((o) => (
              <option key={o.id} value={o.id}>
                {o.code} · {o.title}
              </option>
            ))}
          </select>
          <span className="text-[11px] t-faint">{t('recurring.form.opportunityHint')}</span>
        </label>

        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-xs t-muted">{t('recurring.form.periodicity')}</span>
            <select
              value={periodicity}
              onChange={(e) => setPeriodicity(e.target.value as RecurringInvoicePeriodicityT)}
              className={input}
            >
              {Object.keys(RecurringInvoicePeriodicityLabel).map((k) => (
                <option key={k} value={k}>
                  {t('enums:RecurringInvoicePeriodicity.' + k)}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="text-xs t-muted">{t('recurring.form.nextRun')}</span>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className={input}
            />
          </label>
        </div>

        <label className="block">
          <span className="text-xs t-muted">{t('recurring.form.notes')}</span>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            className={input}
          />
        </label>

        {/* Líneas */}
        <div className="border-t border-base pt-3">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-semibold t-primary">{t('recurring.form.linesHeader')}</h4>
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={() => setPickerOpen(true)}
                disabled={!clientId}
                title={
                  clientId
                    ? t('recurring.form.fromRateTitle')
                    : t('recurring.form.fromRateNoClient')
                }
                className="px-2 py-0.5 rounded text-xs t-secondary border border-base hover:bg-hover hover:text-toga-700 dark:hover:text-toga-200 disabled:opacity-50 flex items-center gap-1"
              >
                <Coins size={11} /> {t('recurring.form.fromRate')}
              </button>
              <button
                type="button"
                onClick={addLine}
                className="px-2 py-0.5 rounded text-xs t-secondary border border-base hover:bg-hover"
              >
                {t('recurring.form.addLine')}
              </button>
            </div>
          </div>
          <RatePickerModal
            open={pickerOpen}
            clientId={clientId || null}
            allowedKinds={['IGUALA_MENSUAL']}
            onClose={() => setPickerOpen(false)}
            onPick={addLineFromRate}
          />
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wider t-faint">
              <tr>
                <th className="px-1 text-left">{t('recurring.form.lineCols.description')}</th>
                <th className="px-1 text-right w-14">{t('recurring.form.lineCols.qty')}</th>
                <th className="px-1 text-right w-20">{t('recurring.form.lineCols.vat')}</th>
                <th
                  className="px-1 text-right w-24 text-amber-400/70"
                  title={t('recurring.form.lineCols.costTitle')}
                >
                  {t('recurring.form.lineCols.cost')} <span className="normal-case text-[9px]">{t('recurring.form.lineCols.intShort')}</span>
                </th>
                <th
                  className="px-1 text-right w-20 text-amber-400/70"
                  title={t('recurring.form.lineCols.marginTitle')}
                >
                  {t('recurring.form.lineCols.margin')}
                </th>
                <th className="px-1 text-right w-24">{t('recurring.form.lineCols.price')}</th>
                <th className="w-6"></th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l, idx) => {
                const qty = Number(l.quantity) || 0
                const lineMargin =
                  Math.round(qty * (l.unitPriceCents ?? 0)) - Math.round(qty * (l.costCents ?? 0))
                return (
                  <tr key={l._key} className="border-t border-base/40">
                    <td className="px-1 py-1">
                      <div className="flex items-center gap-1">
                        {products.length > 0 && (
                          <select
                            value={l.productId ?? ''}
                            onChange={(e) => onPickProduct(idx, e.target.value)}
                            title={t('recurring.form.lineCols.catalogProductTitle')}
                            className={`${input} text-xs w-32 shrink-0`}
                          >
                            <option value="">{t('recurring.form.lineCols.manual')}</option>
                            {products.map((p) => (
                              <option key={p.id} value={p.id}>
                                {p.code} · {p.name}
                              </option>
                            ))}
                          </select>
                        )}
                        <input
                          value={l.description}
                          onChange={(e) => updateLine(idx, { description: e.target.value })}
                          placeholder={t('recurring.form.lineCols.descriptionPlaceholder')}
                          className={`${input} text-xs flex-1`}
                        />
                      </div>
                    </td>
                    <td className="px-1 py-1">
                      <input
                        value={l.quantity}
                        onChange={(e) => updateLine(idx, { quantity: e.target.value })}
                        className={`${input} text-xs text-right`}
                      />
                    </td>
                    <td className="px-1 py-1">
                      <select
                        value={l.vatRate}
                        onChange={(e) => updateLine(idx, { vatRate: e.target.value })}
                        className={`${input} text-xs text-right`}
                      >
                        <option value="0.21">21%</option>
                        <option value="0.10">10%</option>
                        <option value="0.04">4%</option>
                        <option value="0">0%</option>
                      </select>
                    </td>
                    <td className="px-1 py-1">
                      <input
                        value={l.costInput}
                        onChange={(e) =>
                          updateLine(idx, {
                            costInput: e.target.value,
                            costCents: parseEurosToCents(e.target.value),
                          })
                        }
                        placeholder="0,00"
                        title={t('recurring.form.lineCols.costInputTitle')}
                        className="w-full bg-amber-500/[0.04] border border-amber-500/20 rounded px-2 py-1.5 text-xs text-amber-800 dark:text-amber-200/90 text-right font-mono focus:outline-none focus:border-amber-500/40"
                      />
                    </td>
                    <td
                      className="px-1 py-1 text-right text-xs font-mono text-amber-700 dark:text-amber-300/70"
                      title={t('recurring.form.lineCols.lineMarginTitle')}
                    >
                      {formatCents(lineMargin)}
                    </td>
                    <td className="px-1 py-1">
                      <input
                        value={l.unitPriceInput}
                        onChange={(e) =>
                          updateLine(idx, {
                            unitPriceInput: e.target.value,
                            unitPriceCents: parseEurosToCents(e.target.value),
                          })
                        }
                        placeholder="0,00"
                        className={`${input} text-xs text-right font-mono`}
                      />
                    </td>
                    <td className="px-1 py-1 text-right">
                      <button
                        type="button"
                        onClick={() => removeLine(idx)}
                        className="t-muted hover:text-rose-700 dark:hover:text-rose-300"
                        disabled={lines.length === 1}
                      >
                        <Trash2 size={12} />
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>

          <div className="mt-2 text-right text-sm">
            <span className="t-faint">{t('recurring.form.subtotalPerPeriod')}</span>{' '}
            <span className="t-primary font-mono font-semibold">{formatCents(totalCents)}</span>
          </div>
          <div className="mt-1 text-right text-xs text-amber-700 dark:text-amber-300/70">
            <span className="uppercase tracking-wider text-[9px] mr-1">{t('recurring.form.internalTag')}</span>
            {t('recurring.form.costLabel')}{' '}
            <span className="font-mono">{formatCents(totalCostCents)}</span> · {t('recurring.form.marginLabel')}{' '}
            <span className="font-mono text-amber-800 dark:text-amber-200/90">
              {formatCents(totalCents - totalCostCents)}
              {totalCents > 0 && (
                <span className="text-amber-700 dark:text-amber-300/50 ml-0.5">
                  ({(((totalCents - totalCostCents) / totalCents) * 100).toFixed(0)}%)
                </span>
              )}
            </span>
          </div>
        </div>

        {error && <p className="text-xs text-rose-700 dark:text-rose-300">{error}</p>}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 rounded-lg text-sm t-secondary border border-base hover:bg-hover"
          >
            {t('common:actions.cancel')}
          </button>
          <button
            type="submit"
            disabled={busy}
            className="px-3 py-1.5 rounded-lg text-sm font-semibold text-white bg-toga-600 hover:bg-toga-700 disabled:opacity-50 flex items-center gap-1.5"
          >
            {busy && <Loader2 size={14} className="animate-spin" />}
            {isEdit ? t('common:actions.save') : t('common:actions.create')}
          </button>
        </div>
      </form>
    </Modal>
  )
}

void FileText // suprime unused warning
